<script setup lang="ts">
import { useRegisterOptionsStore } from "~/stores/get-register-options-data";
import { type InferType } from "yup";
import { useProfileStore } from "~/stores/get-profile-data";
import { getSchema } from "~/composables/form-schema";
import { splitCellPhone } from "~/composables/register-data";
import { useReCaptcha } from "vue-recaptcha-v3";
import { format } from "date-fns";
import { useI18n } from "vue-i18n";
import { toast } from "vue3-toastify";

const props = defineProps({
  type: {
    type: String,
    default: "create",
  },
  registerButtonIframeTitle: {
    type: String,
    default: "",
  },
  successMessage: {
    type: String,
    default: "",
  },
  disableAfterSubmit: {
    type: Boolean,
    default: false,
  },
});

const schema = getSchema() as any;
const isLoading = ref(true);
const { t, locale } = useI18n();

const buttonTitle = computed(() => {
  if (props.registerButtonIframeTitle !== "" && props.type === "create") {
    return props.registerButtonIframeTitle;
  }
  return props.type === "create"
    ? t("userForm.createButtonTitle")
    : t("userForm.updateButtonTitle");
});

const profileStore = useProfileStore();

if (props.type == "update") {
  if (!profileStore.profile) {
    await profileStore.getData(locale.value);
  }
}

const profile = profileStore.profile as any;
const defaultPrefix = locale.value == "pt" ? "351" : "34";
const profilePhone = splitCellPhone(profile?.customer_cell_phone, defaultPrefix);

const state =
  props.type == "update"
    ? reactive<Schema>({
        email: profile?.email ?? "",
        store: profile?.store ?? "",
        customer_name: profile?.customer_name ?? "",
        customer_surname: profile?.customer_surname ?? "",
        customer_birthday:
          profile?.customer_birthday ?? (formatDate(null) as any),
        cell_phone_prefix: profilePhone.prefix,
        customer_cell_phone: profilePhone.number,
        gdpr_privacy: true,
        customer_gender:
          profile.customer_gender !== "" ? profile.customer_gender : "empty",
        customer_address: profile.customer_address ?? "",
        customer_zip: profile.customer_zip ?? "",
        customer_city: profile.customer_city ?? "",
        customer_province: profile.customer_province ?? "",
      })
    : reactive<Schema>({
        email: "",
        store: "",
        customer_name: "",
        customer_surname: "",
        customer_birthday: formatDate(null) as any,
        cell_phone_prefix: defaultPrefix,
        customer_cell_phone: "",
        gdpr_privacy: false,
        customer_gender: "empty",
        customer_address: "",
        customer_zip: "",
        customer_city: "",
        customer_province: "",
      });

const extraState =
  props.type == "update"
    ? reactive({
        customer_childrens: profile.customer_childrens ?? ([] as any),
        child_birthday_upcoming:
          profile.child_birthday_upcoming ?? (formatDate(null) as any),
        gdpr_newsletter: profile.gdpr_newsletter ?? false,
        gdpr_profiling: profile.gdpr_profiling ?? false,
        gdpr_marketing_analysis: profile.gdpr_marketing_analysis ?? false,
        gdpr_contest_opt_in: profile.gdpr_contest_opt_in ?? false,
        gdpr_external_consent: profile.gdpr_external_consent ?? false,
      })
    : (reactive({
        customer_childrens: [] as any,
        child_birthday_upcoming: formatDate(null) as any,
        gdpr_newsletter: false,
        gdpr_profiling: false,
        gdpr_marketing_analysis: false,
        gdpr_contest_opt_in: false,
        gdpr_external_consent: false,
      }) as any);

const emit = defineEmits(["submit"]);
const store = useRegisterOptionsStore();

await store.getStores();
await store.getMobile();
await store.getTerms();
await store.getProvince();

const [storesOptions, orderedTerms, mobilePrefixesOptions, provinceOptions] = [
  store.storesOptions,
  store.termsOptions,
  store.mobileOptions,
  store.provinceOptions,
];

const genderOptions = reactive([
  {
    value: "empty",
    label: t("profile.genderOptions.notSpecified"),
  },
  {
    value: "M",
    label: t("profile.genderOptions.male"),
  },
  {
    value: "F",
    label: t("profile.genderOptions.female"),
  },
]);

function formatDate(date: string | null) {
  if (date) {
    let d = new Date(date);
    // return d.toISOString().split("T")[0]; // Returns the date part in Y-m-d format
    return format(d, "yyyy-MM-dd");
  }
  return null;
}

type Schema = InferType<typeof schema>;

function addChildren() {
  if (!Array.isArray(extraState.customer_childrens)) {
    extraState.customer_childrens = [];
  }

  extraState.customer_childrens.push({
    child_first_name: null,
    child_gender: "empty",
    child_birthday: formatDate(null) as any,
  });
}

const isSubmitting = ref(false);
const errorMessage = ref("");

const recaptchaInstance = useReCaptcha();

async function submit() {
  const isValid = await schema.isValid(state);
  isSubmitting.value = true;
  errorMessage.value = "";

  await recaptchaInstance?.recaptchaLoaded();
  const token = await recaptchaInstance?.executeRecaptcha("submit");

  if (token) {
    try {
      const params = new URLSearchParams();
      params.append("response", token);
      const response = await $fetch("/api/verify-recaptcha", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        params: {
          response: token,
        },
      });

      if (response.success) {
        if (isValid) {
          if (state.customer_birthday) {
            state.customer_birthday = formatDate(state.customer_birthday);
          }

          if (extraState.child_birthday_upcoming) {
            extraState.child_birthday_upcoming = formatDate(
              extraState.child_birthday_upcoming
            );
          }

          if (extraState?.customer_childrens?.length > 0) {
            extraState.customer_childrens.map((child: any) => {
              if (child.child_birthday) {
                child.child_birthday = formatDate(child.child_birthday);
              }
              if (child.child_gender == "") {
                child.child_gender = "empty";
              }

              return child;
            });
          } else {
            extraState.customer_childrens = [];
          }

          state.customer_cell_phone =
            state.cell_phone_prefix + "-" + state.customer_cell_phone;

          const data = { ...state, ...extraState };
          const phone = splitCellPhone(state.customer_cell_phone, defaultPrefix);

          emit("submit", data);

          state.cell_phone_prefix = phone.prefix;
          state.customer_cell_phone = phone.number;
        } else {
          errorMessage.value =
            "Ocurrió un error, revisa los campos del formulario";
        }
      } else {
        throw new Error("reCAPTCHA verification failed.");
      }
    } catch (error) {
      console.log("error", error);
    }
  } else {
    throw new Error("reCAPTCHA token generation failed.");
  }

  setTimeout(() => (isSubmitting.value = false), 2000);
}

onMounted(() => {
  // Check if the locale is loaded before displaying the options
  if (locale.value) {
    isLoading.value = false;
  }
});

const shouldDisableForm = computed(() => {
  return props.disableAfterSubmit;
});

const fieldLabels: Record<string, string> = {
  store: t('userForm.fields.store'),
  customer_name: t('userForm.fields.name'),
  customer_surname: t('userForm.fields.surname'),
  customer_birthday: t('userForm.fields.birthday'),
  customer_gender: t('userForm.fields.gender'),
  email: t('userForm.fields.email'),
  cell_phone_prefix: t('userForm.fields.prefix'),
  customer_cell_phone: t('userForm.fields.mobileNr'),
  first_name: "Child Name",
  birthday: "Child Birthday",
  gender: "Child Gender",
  child_birthday_upcoming: t("userForm.expectedBirthDate"),
  customer_address: t('userForm.fields.address'),
  customer_zip: t('userForm.fields.zip'),
  customer_city: t('userForm.fields.city'),
  customer_province: t('userForm.fields.province'),
  gdpr_privacy: t('userForm.fields.gdpr'),
};

function onFormError(errors: any) {
  if (errors.errors && errors.errors.length) {
    const errorFields = errors.errors.map((err: any) => {
      return fieldLabels[err.path] || err.path;
    });

    const uniqueFields = Array.from(new Set(errorFields));

    const fieldsText = uniqueFields.join(", ");

    const errorPrefix= t('userForm.fields.errorPrefix')
    toast.error(`${errorPrefix}: ${fieldsText}`, {
  autoClose: 10000,
});
  }
}
</script>
<template>
  <UForm
    v-if="!isLoading"
    :schema="schema"
    :state="state"
    @submit="submit"
    @error="onFormError"
    :class="{ 'opacity-50 pointer-events-none': shouldDisableForm }"
    class="w-full bg-[#f9faf7] py-10 mb-10 px-6 md:px-12"
    id="top-of-form"
  >
    <div class="max-w-screen-lg mx-auto space-y-8">
      <div>
        <div class="flex gap-1 items-center">
          <nuxt-img
            class="size-8 md:size-12"
            src="images/registration/lobby.svg"
          />
          <h1
            class="uppercase text-brand-secondary font-semibold text-lg md:text-3xl"
          >
            {{ t("userForm.selectStore") }}
          </h1>
        </div>

        <div class="my-6">
          <UFormGroup
            label="Selecciona la tienda preferida / más cercana*"
            name="store"
          >
            <CoreSelectField
              placeholder="Seleccionar"
              :disabled="shouldDisableForm"
              v-model="state.store"
              :options="storesOptions"
              option-attribute="label"
            />
          </UFormGroup>
        </div>
      </div>
      <div>
        <div class="flex items-center gap-2">
          <nuxt-img
            class="size-8 md:size-12"
            src="images/registration/edit.svg"
          />

          <h1
            class="uppercase text-brand-secondary font-semibold text-lg md:text-3xl"
          >
            {{ t("userForm.yourData") }}
          </h1>
        </div>

        <div class="my-6 space-y-6">
          <UFormGroup
            :label="t('userForm.fields.name') + ' *'"
            name="customer_name"
          >
            <CoreInputField
              v-model="state.customer_name"
              :disabled="shouldDisableForm"
              :placeholder="t('userForm.fields.name')"
            />
          </UFormGroup>

          <UFormGroup
            :label="t('userForm.fields.surname') + ' *'"
            name="customer_surname"
          >
            <CoreInputField
              v-model="state.customer_surname"
              :disabled="shouldDisableForm"
              :placeholder="t('userForm.fields.surname')"
            />
          </UFormGroup>
          <UFormGroup
            :label="t('userForm.fields.birthday') + ' *'"
            name="customer_birthday"
          >
            <UPopover :popper="{ placement: 'bottom-start' }">
              <CoreDateLabel :label="state.customer_birthday" />
              <template #panel="{ close }">
                <CoreDatepicker
                  v-model="state.customer_birthday"
                  @close="close"
                />
              </template>
            </UPopover>
          </UFormGroup>
          <UFormGroup
            :label="t('userForm.fields.gender')"
            name="customer_gender"
          >
            <CoreSelectField
              :placeholder="t('userForm.fields.select')"
              :options="genderOptions"
              :disabled="shouldDisableForm"
              v-model="state.customer_gender"
              option-attribute="label"
            />
          </UFormGroup>
          <UFormGroup :label="t('userForm.fields.email') + ' *'" name="email">
            <CoreInputField
              v-model="state.email"
              :disabled="shouldDisableForm"
              :placeholder="t('userForm.fields.email')"
              :toLowerCase="type=='create'"
            />
          </UFormGroup>
        </div>

        <div class="my-5 space-y-6 bg-white rounded-sm p-4">
          <div class="flex flex-col md:flex-row gap-3">
            <UFormGroup
              :label="t('userForm.fields.prefix') + ' *'"
              name="cell_phone_prefix"
              class="w-full md:w-1/4"
            >
              <CoreSelectField
                :placeholder="t('userForm.fields.select')"
                additional-classes="!border-[1px] !border-dove-gray-300 ring-!transparent"
                variant="outline"
                :disabled="shouldDisableForm"
                :options="mobilePrefixesOptions"
                option-attribute="country"
                v-model="state.cell_phone_prefix"
              />
            </UFormGroup>
            <UFormGroup
              class="w-full"
              :label="t('userForm.fields.mobileNr') + ' *'"
              name="customer_cell_phone"
            >
              <CoreInputField
                additional-classes="!border-[1px] !border-dove-gray-300 ring-!transparent"
                variant="outline"
                :disabled="shouldDisableForm"
                v-model="state.customer_cell_phone"
                :placeholder="t('userForm.fields.mobileNr')"
              />
            </UFormGroup>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <nuxt-img
          class="size-8 md:size-12"
          src="images/registration/family.svg"
        />
        <h1
          class="uppercase text-brand-secondary font-semibold text-lg md:text-3xl"
        >
          {{ t("userForm.yourFamily") }}
        </h1>
      </div>
      <div class="p-4 rounded-sm bg-white my-6">
        <div>
          <TransitionGroup name="slide-fade">
            <template
              v-for="(child, i) in extraState.customer_childrens"
              :key="i"
            >
              <div
                class="flex flex-col lg:flex-row lg:flex-shrink gap-2 lg:items-center my-1"
              >
                <div class="grid lg:grow lg:grid-cols-3 gap-3">
                  <UFormGroup
                    :label="t('userForm.fields.name')"
                    name="first_name"
                  >
                    <CoreInputField
                      variant="outline"
                      :disabled="shouldDisableForm"
                      additional-classes="!border[0.5px] !border-dove-gray-300"
                      v-model="child.child_first_name"
                      :placeholder="t('userForm.fields.name')"
                    />
                  </UFormGroup>
                  <UFormGroup
                    :label="t('userForm.fields.birthday')"
                    name="birthday"
                  >
                    <UPopover :popper="{ placement: 'bottom-start' }">
                      <CoreDateLabel
                        :label="child.child_birthday"
                        class="!ring-[1px] !ring-dove-gray-300"
                      />
                      <template #panel="{ close }">
                        <core-datepicker
                          v-model="child.child_birthday"
                          @close="close"
                        />
                      </template>
                    </UPopover>
                  </UFormGroup>
                  <UFormGroup
                    :label="t('userForm.fields.gender')"
                    name="gender"
                  >
                    <CoreSelectField
                      :placeholder="t('userForm.fields.select')"
                      variant="outline"
                      :disabled="shouldDisableForm"
                      v-model="child.child_gender"
                      additional-classes="!border[0.5px] !border-dove-gray-300"
                      :options="genderOptions"
                      option-attribute="label"
                    />
                  </UFormGroup>
                </div>
                <button
                  :disabled="shouldDisableForm"
                  class="hidden cursor-pointer size-6 bg-cerise-red-600 lg:flex items-center rounded-full text-white justify-center mt-6"
                  @click="extraState.customer_childrens.splice(i, 1)"
                >
                  <nuxt-img
                    src="images/close-white.svg"
                    class="size-3"
                    fit="cover"
                  />
                </button>
                <button
                  :disabled="shouldDisableForm"
                  class="lg:hidden cursor-pointer text-cerise-red-600 text-sm font-semibold text-center flex justify-center my-5"
                  @click="extraState.customer_childrens.splice(i, 1)"
                >
                  {{ t("userForm.removeChildren") }}
                </button>
              </div>
            </template>
          </TransitionGroup>
          <div
            class="flex flex-col mt-8 justify-center items-center gap-x-7 md:gap-y-9 gap-y-4"
          >
            <core-button
              @click="addChildren"
              :disabled="shouldDisableForm"
              class="rounded-sm bg-gradient-to-r from-20% from-[#E72B6F] to-[#1450A0] to-80% w-fit text-sm"
            >
              {{ t("userForm.addChildren") }}
            </core-button>

            <ProfileUpdateWhyImportant
              :text="t('userForm.whyImportant.description')"
              iconClass="md:!mb-10 !mb-16"
              class="pb-6 flex gap-3"
            />
          </div>
        </div>
      </div>

      <div>
        <h1 class="text-brand-secondary font-semibold text-lg md:text-3xl">
          {{ t("userForm.expectedBirthDate") }}
        </h1>
        <div class="my-6">
          <UFormGroup
            :label="t('userForm.fields.expectedBirthDate')"
            name="child_birthday_upcoming"
          >
            <UPopover :popper="{ placement: 'bottom-start' }">
              <CoreDateLabel :label="extraState.child_birthday_upcoming" />
              <template #panel="{ close }">
                <CoreDatepicker
                  v-model="extraState.child_birthday_upcoming"
                  @close="close"
                />
              </template>
            </UPopover>
          </UFormGroup>
        </div>
      </div>

      <div>
        <div class="flex items-center gap-2">
          <nuxt-img
            class="size-8 md:size-10"
            src="images/registration/home.svg"
          />
          <h1
            class="text-brand-secondary font-semibold text-lg md:text-3xl uppercase"
          >
            {{ t("userForm.additionalData") }}
          </h1>
        </div>
        <div class="my-6">
          <UFormGroup
            :label="t('userForm.fields.address')"
            name="customer_address"
          >
            <CoreInputField
              v-model="state.customer_address"
              :disabled="shouldDisableForm"
              :placeholder="t('userForm.fields.address')"
            />
          </UFormGroup>
        </div>
        <div class="flex flex-col md:flex-row justify-between gap-3">
          <UFormGroup
            class="md:w-1/3"
            :label="t('userForm.fields.zip')"
            name="customer_zip"
          >
            <CoreInputField
              v-model="state.customer_zip"
              :disabled="shouldDisableForm"
              :placeholder="t('userForm.fields.zip')"
            />
          </UFormGroup>
          <UFormGroup
            class="md:w-1/3"
            :label="t('userForm.fields.city')"
            name="customer_city"
          >
            <CoreInputField
              v-model="state.customer_city"
              :disabled="shouldDisableForm"
              :placeholder="t('userForm.fields.city')"
            />
          </UFormGroup>

          <UFormGroup
            :label="t('userForm.fields.province')"
            name="customer_province"
            class="md:w-1/3"
          >
            <CoreSelectField
              :options="[
                {
                  province: t('userForm.fields.provincePlaceholder'),
                  value: '',
                },
                ...provinceOptions,
              ]"
              :disabled="shouldDisableForm"
              option-attribute="province"
              v-model="state.customer_province"
            />
          </UFormGroup>
        </div>
      </div>

      <div>
        <div class="flex items-center gap-2">
          <nuxt-img
            class="size-8 md:size-12"
            src="images/registration/data.svg"
          />
          <h1
            class="text-brand-secondary font-semibold text-lg md:text-3xl uppercase"
          >
            {{ t("userForm.consent") }}
          </h1>
        </div>
        <div class="space-y-5 my-6 md:space-y-6">
          <template v-for="(item, i) in orderedTerms" :key="i">
            <UFormGroup :name="item.name">
              <ProfileUpdateConsent
                :class="props.type !== 'create' ? 'hidden' : ''"
                v-if="item.name == 'gdpr_privacy'"
                v-model="state.gdpr_privacy"
                :primaryTitle="item.title"
                :description="item.description"
                :secondaryTitle="item.warning"
              />
              <ProfileUpdateConsent
                v-else
                v-model="extraState[item.name]"
                :primaryTitle="item.title"
                :description="item.description"
                :secondaryTitle="item.warning"
              />
            </UFormGroup>
          </template>
        </div>
      </div>
    </div>
    <div class="flex justify-center">
      <button
        type="submit"
        :disabled="isSubmitting || shouldDisableForm"
        class="w-fit px-3 md:w-1/3 rounded-sm border py-2 md:py-4 disabled:cursor-not-allowed disabled:opacity-25 text-white bg-gradient-to-r from-20% from-[#E72B6F] to-[#1450A0] to-80%"
      >
        {{ buttonTitle }}
      </button>
    </div>
    <div v-if="successMessage" class="flex justify-center my-5">
      <h3 class="text-lg text-green-700">{{ successMessage }}</h3>
    </div>

    <div v-if="errorMessage" class="flex justify-center my-5">
      <h3 class="text-lg text-red-700">{{ errorMessage }}</h3>
    </div>
  </UForm>
</template>
